import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FlightAlertsHeader } from "./FlightAlertsHeader";
import { StepIndicator } from "./StepIndicator";
import { HkiaAttribution } from "./HkiaAttribution";
import { BotCheckStep } from "./BotCheckStep";
import { DirectionStep } from "./DirectionStep";
import { FlightFormStep } from "./FlightFormStep";
import { ResultCard } from "./ResultCard";
import { OptInStep } from "./OptInStep";
import { VerifyStep } from "./VerifyStep";
import { ConfirmedStep } from "./ConfirmedStep";
import { useFlightLookup } from "./handler/useFlightLookup";
import { useRecaptcha } from "./handler/useRecaptcha";
import { useOtpSend, OtpOutcome } from "./handler/useOtpSend";
import { useOtpVerify } from "./handler/useOtpVerify";
import { useConfirmWatch } from "./handler/useConfirmWatch";
import { toBackendDirection } from "./handler/mapFlightCardResponse";
import {
  BotCheckState,
  FlightDirection,
  FlightFixture,
  WizardStep,
} from "./types";

const STEP_NUMBER: Partial<Record<WizardStep, number>> = {
  botCheck: 1,
  direction: 2,
  flightForm: 3,
};

interface LastQuery {
  flightNumber: string;
  isoDate: string;
}

const FlightAlerts: React.FC = () => {
  const [searchParams] = useSearchParams();
  const forcedBotCheck = searchParams.get("botCheck");
  const isForcedFailed = forcedBotCheck === "failed";
  const isForcedUnavailable = forcedBotCheck === "unavailable";

  const [wizardStep, setWizardStep] = useState<WizardStep>(
    isForcedUnavailable ? "direction" : "botCheck",
  );
  const [botCheckState, setBotCheckState] = useState<BotCheckState>(
    isForcedFailed ? "failed" : isForcedUnavailable ? "unavailable" : "pending",
  );
  const [direction, setDirection] = useState<FlightDirection | null>(null);
  const [lastQuery, setLastQuery] = useState<LastQuery | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedFixture, setSelectedFixture] = useState<FlightFixture | null>(
    null,
  );
  const [isBotCheckVerifying, setIsBotCheckVerifying] = useState(false);
  const { getToken } = useRecaptcha();
  const { data, isLoading, error, lookup, reset } = useFlightLookup(getToken);
  const { sendOtp, isSending } = useOtpSend(getToken);
  const { verifyOtp, isVerifying } = useOtpVerify();
  const { confirmWatch, isConfirming } = useConfirmWatch();

  const handleBotCheckChange = (next: BotCheckState) => {
    if (next === "checked") {
      setIsBotCheckVerifying(true);
      getToken("flight_lookup")
        .then(() => {
          setBotCheckState("checked");
          setWizardStep("direction");
        })
        .catch(() => {
          setBotCheckState("unavailable");
          setWizardStep("direction");
        })
        .finally(() => setIsBotCheckVerifying(false));
      return;
    }
    setBotCheckState(next);
    if (next === "unavailable") {
      setWizardStep("direction");
    }
  };

  const handleDirectionSelect = (next: FlightDirection) => {
    setDirection(next);
    setWizardStep("flightForm");
  };

  const handleFlightSubmit = (flightNumber: string, isoDate: string) => {
    if (!direction) return;
    setLastQuery({ flightNumber, isoDate });
    lookup(direction, flightNumber, isoDate);
  };

  const handleChangeDirection = () => {
    reset();
    setDirection(null);
    setSelectedFixture(null);
    setWizardStep("direction");
  };

  const handleStartOptIn = (fixture: FlightFixture) => {
    setSelectedFixture(fixture);
    setWizardStep("optIn");
  };

  const handleOptInSubmit = async (phone: string): Promise<OtpOutcome> => {
    const result = await sendOtp(phone);
    if (result.success) {
      setPhoneNumber(phone);
      setWizardStep("verify");
    }
    return result;
  };

  const handleResendOtp = (): Promise<OtpOutcome> => sendOtp(phoneNumber);

  const handleVerifyCode = async (code: string): Promise<OtpOutcome> => {
    const verifyResult = await verifyOtp(phoneNumber, code);
    if (!verifyResult.success) return verifyResult;
    if (!selectedFixture) return { success: false };

    const watchResult = await confirmWatch({
      phoneNumber,
      direction: toBackendDirection(selectedFixture.direction),
      operatingDesignator: selectedFixture.flightNumber,
      flightDate: selectedFixture.isoDate,
    });
    if (!watchResult.success) return watchResult;

    setWizardStep("confirmed");
    return { success: true };
  };

  const handleRetryFeed = () => {
    if (!direction || !lastQuery) return;
    lookup(direction, lastQuery.flightNumber, lastQuery.isoDate);
  };

  const handleLookupAnother = () => {
    reset();
    setSelectedFixture(null);
    setWizardStep("flightForm");
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setWizardStep("result");
    }
  }, [data]);

  const stepNumber = STEP_NUMBER[wizardStep];

  return (
    <div className="min-h-screen bg-app flex flex-col items-center px-6 pt-[74px] pb-6 md:pt-20 md:pb-20">
      <div className="w-full max-w-[600px] flex flex-col gap-5 md:gap-6">
        <FlightAlertsHeader />
        {stepNumber && (
          <StepIndicator
            current={stepNumber}
            total={3}
            botCheckState={botCheckState}
          />
        )}
        {wizardStep === "botCheck" && (
          <BotCheckStep
            botCheckState={botCheckState}
            onChange={handleBotCheckChange}
            isVerifying={isBotCheckVerifying}
          />
        )}
        {wizardStep === "direction" && (
          <DirectionStep onSelect={handleDirectionSelect} />
        )}
        {wizardStep === "flightForm" && direction && (
          <FlightFormStep
            direction={direction}
            error={error}
            isLoading={isLoading}
            onSubmit={handleFlightSubmit}
            onChangeDirection={handleChangeDirection}
          />
        )}
        {wizardStep === "result" && data && data.length > 0 && (
          <ResultCard
            fixtures={data}
            botCheckState={botCheckState}
            onStartOptIn={handleStartOptIn}
            onRetryFeed={handleRetryFeed}
            onLookupAnother={handleLookupAnother}
          />
        )}
        {wizardStep === "optIn" && selectedFixture && (
          <OptInStep
            fixture={selectedFixture}
            onSubmit={handleOptInSubmit}
            isSubmitting={isSending}
            onBack={() => setWizardStep("result")}
          />
        )}
        {wizardStep === "verify" && selectedFixture && (
          <VerifyStep
            phoneNumber={phoneNumber}
            flightNumber={selectedFixture.flightNumber}
            onVerify={handleVerifyCode}
            onResend={handleResendOtp}
            isVerifying={isVerifying || isConfirming}
            onBack={() => setWizardStep("result")}
          />
        )}
        {wizardStep === "confirmed" && selectedFixture && (
          <ConfirmedStep fixture={selectedFixture} phoneNumber={phoneNumber} />
        )}
        {botCheckState !== "failed" && <HkiaAttribution />}
      </div>
    </div>
  );
};

export default FlightAlerts;